import { S } from './app_state.js?v=20260817c';

const VALID_SCENE_PRESETS = new Set(['custom', 'datacenter', 'basement', 'outdoor']);

let scene3DModule = null;
let scene3DModulePromise = null;
let view3DControllerModule = null;
let view3DControllerModulePromise = null;
let threePdfExportModalModule = null;
let threePdfExportModalModulePromise = null;
let materialLibraryWarmupStarted = false;

function loadScene3D() {
  if (scene3DModule) return Promise.resolve(scene3DModule);
  if (!scene3DModulePromise) {
    scene3DModulePromise = import('../three/scene3d.js?v=20260904f')
      .then(module => {
        scene3DModule = module;
        warmupMaterialLibrary();
        return module;
      })
      .finally(() => {
        scene3DModulePromise = null;
      });
  }
  return scene3DModulePromise;
}

function loadView3DController() {
  if (view3DControllerModule) return Promise.resolve(view3DControllerModule);
  if (!view3DControllerModulePromise) {
    view3DControllerModulePromise = Promise.all([
      import('../three/view3d_controller.js?v=20260904f'),
      loadScene3D()
    ])
      .then(([module]) => {
        view3DControllerModule = module;
        return module;
      })
      .finally(() => {
        view3DControllerModulePromise = null;
      });
  }
  return view3DControllerModulePromise;
}

function loadThreePdfExportModal() {
  if (threePdfExportModalModule) return Promise.resolve(threePdfExportModalModule);
  if (!threePdfExportModalModulePromise) {
    threePdfExportModalModulePromise = import('../three/three_pdf_export_modal.js?v=20260904f')
      .then(module => {
        threePdfExportModalModule = module;
        return module;
      })
      .finally(() => {
        threePdfExportModalModulePromise = null;
      });
  }
  return threePdfExportModalModulePromise;
}

function warmupMaterialLibrary() {
  if (materialLibraryWarmupStarted) return;
  materialLibraryWarmupStarted = true;
  import('../three/three_material_library.js?v=20260710a')
    .then(module => module.loadMaterialLibrary?.())
    .catch(error => console.warn('[KS Studio] Materialbibliothek konnte nicht geladen werden.', error));
}

function is3DModalOpen() {
  return Boolean(document.getElementById('threeDModalRoot'));
}

function activeProjectScenePreset() {
  const key = String(S.project?.levels?.environmentPreset || 'custom').trim().toLowerCase();
  return VALID_SCENE_PRESETS.has(key) ? key : 'custom';
}

function current3DModalScenePreset(view3d = S.view3d) {
  if (scene3DModule?.current3DModalScenePreset) {
    return scene3DModule.current3DModalScenePreset(view3d);
  }
  const key = String(view3d?.modalScenePreset || '').trim().toLowerCase();
  if (VALID_SCENE_PRESETS.has(key)) return key;
  const fallback = activeProjectScenePreset();
  if (view3d && typeof view3d === 'object') view3d.modalScenePreset = fallback;
  return fallback;
}

function default3DModalFilters() {
  return {
    floor: true,
    doubleFloor: true,
    doubleCeiling: true,
    ceiling: false
  };
}

function normalize3DModalFilters(view3d = S.view3d) {
  if (scene3DModule?.normalize3DModalFilters) {
    return scene3DModule.normalize3DModalFilters(view3d);
  }
  if (!view3d || typeof view3d !== 'object') return default3DModalFilters();
  const source = view3d.modalFilters && typeof view3d.modalFilters === 'object'
    ? view3d.modalFilters
    : {};
  const defaults = default3DModalFilters();
  view3d.modalFilters = Object.fromEntries(
    Object.keys(defaults).map(key => [key, typeof source[key] === 'boolean' ? source[key] : defaults[key]])
  );
  return view3d.modalFilters;
}

function set3DModalScenePreset(presetId) {
  const key = String(presetId || '').trim().toLowerCase();
  if (!VALID_SCENE_PRESETS.has(key)) return false;
  if (scene3DModule?.set3DModalScenePreset) return scene3DModule.set3DModalScenePreset(key);
  if (S.view3d && typeof S.view3d === 'object') {
    S.view3d.modalScenePreset = key;
    S.view3d.modalBackgroundMode = null;
    S.view3d.modalFilters = default3DModalFilters();
  }
  if (is3DModalOpen()) {
    loadScene3D().then(module => module.set3DModalScenePreset?.(key));
  }
  return true;
}

function requestCurrent3DFrame(options = {}) {
  return scene3DModule?.requestCurrent3DFrame?.(options);
}

function render3D(options = {}) {
  if (options.force !== true && !is3DModalOpen()) return Promise.resolve(null);
  return loadScene3D().then(module => module.render3D?.(options));
}

function update3DLighting(options = {}) {
  if (!scene3DModule || !S.view3d?.initialized) return false;
  scene3DModule.update3DLightingForBounds?.(S.view3d?.bounds, options);
  scene3DModule.requestCurrent3DFrame?.({
    skipShadowUpdate: options?.brightnessOnly === true || options?.skipShadowUpdate === true
  });
  return true;
}

function reapply3DGroupVisibilityAfterSceneUpdate() {
  return view3DControllerModule?.reapply3DGroupVisibilityAfterSceneUpdate?.();
}

function sync3DArticleDetail(selection = null) {
  return view3DControllerModule?.sync3DArticleDetail?.(selection);
}

async function invokeView3DAction(method, ...args) {
  const module = await loadView3DController();
  const action = module?.[method];
  if (typeof action !== 'function') throw new Error(`3D-Aktion nicht verfügbar: ${method}`);
  return action(...args);
}

async function openThreePdfExportModal() {
  const module = await loadThreePdfExportModal();
  if (typeof module?.open3DPdfExportModal !== 'function') {
    throw new Error('3D-PDF-Dialog ist nicht verfügbar.');
  }
  return module.open3DPdfExportModal();
}

export {
  current3DModalScenePreset,
  invokeView3DAction,
  is3DModalOpen,
  loadScene3D,
  loadView3DController,
  normalize3DModalFilters,
  openThreePdfExportModal,
  reapply3DGroupVisibilityAfterSceneUpdate,
  render3D,
  requestCurrent3DFrame,
  set3DModalScenePreset,
  sync3DArticleDetail,
  update3DLighting
};
